'use client';

/**
 * =============================================================================
 * RecentlyViewedTracker
 * =============================================================================
 *
 * Client-side component that tracks product views by setting a cookie.
 * The cookie is read server-side by the orchestrator to fetch recently viewed
 * products during SSR.
 *
 * This component renders nothing - it only sets the cookie on mount.
 *
 * Usage:
 *   <RecentlyViewedTracker sku={productSku} />
 *
 * =============================================================================
 */

import { addToRecentlyViewed } from '@/lib/recentlyViewed/client';
import { useEffect } from 'react';

interface RecentlyViewedTrackerProps {
  /** Product SKU to track as recently viewed */
  sku: string;
}

/**
 * Tracks product views by adding the SKU to the recently viewed cookie.
 * Renders nothing - purely for side effects.
 */
export function RecentlyViewedTracker({ sku }: RecentlyViewedTrackerProps) {
  useEffect(() => {
    if (sku) {
      addToRecentlyViewed(sku);
    }
  }, [sku]);

  // Render nothing - this is just for tracking
  return null;
}

export default RecentlyViewedTracker;
